/**
 * Note/Communication Log data model
 * Represents communication logs and notes for deals
 */

export interface Note {
  id: string;
  tenantId: string; // Partition key for Cosmos DB
  dealId: string; // Parent deal reference
  userId: string; // Author
  note: string; // Encrypted if sensitive
  isEncrypted: boolean;
  noteType: NoteType;
  attachments?: Attachment[];
  metadata?: NoteMetadata;
  createdAt: string;
  updatedAt: string;
}

export type NoteType =
  | 'general'
  | 'call'
  | 'email'
  | 'meeting'
  | 'internal'
  | 'deliverable_update'
  | 'attachment'; // Note with only attachments, no text

export interface NoteMetadata {
  sentiment?: 'positive' | 'neutral' | 'negative';
  tags?: string[];
  relatedDeliverableId?: string;
}

/**
 * File attachment for notes
 */
export interface Attachment {
  id: string;
  fileName: string;
  fileType: AttachmentType;
  mimeType: string;
  size: number; // bytes
  blobPath: string; // Path in Azure Blob Storage
  thumbnailPath?: string; // For images/videos
  uploadedAt: string;
}

export type AttachmentType = 'document' | 'image' | 'video';

/**
 * Allowed file extensions per attachment type.
 * Size limits and per-note counts are defined per-SKU and must not be hardcoded here.
 */
export const ATTACHMENT_LIMITS = {
  document: {
    extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'],
  },
  image: {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  },
  video: {
    extensions: ['mp4', 'mov', 'webm'],
  },
} as const;

export interface CreateNoteRequest {
  dealId: string;
  note?: string;
  noteType?: NoteType;
  attachmentIds?: string[]; // IDs of pre-uploaded attachments to link
  attachments?: Attachment[]; // Full attachment objects (preferred over IDs)
  metadata?: NoteMetadata;
}

export interface UpdateNoteRequest {
  note?: string;
  noteType?: NoteType;
  metadata?: NoteMetadata;
  attachments?: Attachment[];
}

/**
 * Upload attachment request - get SAS URL
 */
export interface UploadAttachmentRequest {
  dealId?: string;
  featureRequestId?: string;
  fileName: string;
  mimeType: string;
  size: number;
}

export interface UploadAttachmentResponse {
  success: boolean;
  attachmentId: string;
  uploadUrl: string; // SAS URL for direct upload
  expiresAt: string;
}

/**
 * Confirm attachment upload
 */
export interface ConfirmAttachmentRequest {
  attachmentId: string;
}

export interface ConfirmAttachmentResponse {
  success: boolean;
  attachment: Attachment;
}

/**
 * Get download URL for attachment
 */
export interface DownloadAttachmentResponse {
  success: boolean;
  downloadUrl: string;
  expiresAt: string;
}
